import moment from 'moment';
import { IEntityTerminationTask } from '../types';

/**
 * Normalizes a date-ish value (ISO string, Date, or nullish) to a local
 * start-of-day Date. Returns undefined for null/empty/invalid inputs.
 *
 * For ISO strings we take the leading `YYYY-MM-DD` portion so the returned
 * Date is the calendar day the string represents, independent of the caller's
 * timezone. For Date inputs we use local start-of-day.
 */
export const parseCalendarDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  const parsed =
    typeof value === 'string'
      ? moment(value.split('T')[0], 'YYYY-MM-DD', true)
      : moment(value).startOf('day');
  return parsed.isValid() ? parsed.toDate() : undefined;
};

/**
 * Wraps a react-hook-form `validate` function so it only fires when the current
 * value differs from the persisted initial value. Prevents legacy rows whose
 * persisted date already violates the rule from being blocked in the UI when
 * the user edits an unrelated field (mirrors the server's "only fire when
 * changed" guard).
 */
export const onlyWhenChanged =
  (
    initial: Date | string | null | undefined,
    validator: (value: Date | string | null | undefined) => true | string,
  ) =>
  (value: Date | string | null | undefined): true | string => {
    const initialParsed = parseCalendarDate(initial);
    const currentParsed = parseCalendarDate(value);
    if (!initialParsed && !currentParsed) return true;
    if (initialParsed && currentParsed && initialParsed.getTime() === currentParsed.getTime()) {
      return true;
    }
    return validator(value);
  };

/**
 * Reduces the tasks' Completed Dates to a single extreme value (min or max),
 * ignoring tasks without a completed date and any invalid/sentinel dates.
 */
const reduceTaskCompletedDates = (
  tasks: IEntityTerminationTask[] | undefined,
  keepNew: (current: Date, candidate: Date) => boolean,
): Date | undefined =>
  (tasks ?? [])
    .map((task) => task?.completedDate)
    .filter((date): date is string => !!date)
    .map((date) => parseCalendarDate(date))
    .filter((date): date is Date => !!date)
    .reduce<Date | undefined>(
      (current, candidate) => (!current || keepNew(current, candidate) ? candidate : current),
      undefined,
    );

/**
 * Returns the latest (max) task Completed Date as a local Date, ignoring tasks
 * without a completed date and any invalid/sentinel dates.
 */
export const getLatestTaskCompletedDate = (tasks?: IEntityTerminationTask[]): Date | undefined =>
  reduceTaskCompletedDates(tasks, (current, candidate) => candidate > current);

/**
 * Returns the earliest (min) task Completed Date as a local Date, ignoring
 * tasks without a completed date and any invalid/sentinel dates. Used as the
 * upper bound for a parent Action Item's Date Started.
 */
export const getEarliestTaskCompletedDate = (tasks?: IEntityTerminationTask[]): Date | undefined =>
  reduceTaskCompletedDates(tasks, (current, candidate) => candidate < current);

/**
 * Shared react-hook-form `validate` factory: ensures a picked date is within a
 * one-sided bound. `direction: 'after'` returns an error when the picked date
 * is before the bound; `direction: 'before'` when it is after the bound.
 * Returns true when there is no value or no bound.
 */
const validateAgainstBound =
  (
    bound: Date | undefined,
    direction: 'after' | 'before',
    sourceLabel: string,
    fieldLabel = 'Date',
  ) =>
  (value: Date | string | null | undefined): true | string => {
    if (!value || !bound) return true;
    const picked = parseCalendarDate(value);
    if (!picked) return true;
    const violated = direction === 'after' ? picked < bound : picked > bound;
    if (violated) {
      return `${fieldLabel} must be on or ${direction} the ${sourceLabel} (${moment(bound).format(
        'MM/DD/YYYY',
      )}).`;
    }
    return true;
  };

/**
 * Shared react-hook-form `validate` factory: ensures a picked date is on/after
 * the supplied minDate. Returns true when there is no value or no minDate.
 */
export const validateNotBeforeMinDate = (
  minDate: Date | undefined,
  sourceLabel: string,
  fieldLabel = 'Date',
) => validateAgainstBound(minDate, 'after', sourceLabel, fieldLabel);

/**
 * Shared react-hook-form `validate` factory: ensures a picked date is on/before
 * the supplied maxDate. Used to enforce a parent Date Started ≤ earliest child
 * task Completed Date.
 */
export const validateNotAfterMaxDate = (
  maxDate: Date | undefined,
  sourceLabel: string,
  fieldLabel = 'Date',
) => validateAgainstBound(maxDate, 'before', sourceLabel, fieldLabel);

/**
 * Returns the minimum selectable Date Completed: the later of the Date Started
 * (minCompletedDate) and the latest task Completed Date.
 */
export const getMinDateCompleted = (
  minCompletedDate: Date | undefined,
  latestTaskCompletedDate: Date | undefined,
): Date | undefined =>
  [minCompletedDate, latestTaskCompletedDate]
    .filter((date): date is Date => !!date)
    .reduce<Date | undefined>((max, date) => (!max || date > max ? date : max), undefined);

/**
 * Shared react-hook-form `validate` for the Date Completed field. Ensures a date is
 * supplied when completed and that it is on/after the latest task Completed Date.
 *
 * @param entityLabel phrase used in the required message, e.g. `'a blocker'` or `'an action item'`.
 */
export const validateDateCompleted = (
  value: unknown,
  completed: boolean,
  latestTaskCompletedDate: Date | undefined,
  entityLabel: string,
): string | boolean => {
  if (!completed) return true;
  if (!value) return `Date required when ${entityLabel} is completed.`;
  if (latestTaskCompletedDate) {
    const selectedDate = parseCalendarDate(value as Date | string | null | undefined);
    if (selectedDate && selectedDate < latestTaskCompletedDate) {
      return `Date Completed must be on or after the latest task Completed Date (${moment(
        latestTaskCompletedDate,
      ).format('MM/DD/YYYY')}).`;
    }
  }
  return true;
};
